/**
 * NFC Helper Functions
 * Web NFC (NDEFReader) — NDEF e-ID tags + proper ICAO 9303 MRZ parsing
 *
 * ⚠️  Browser Limitation:
 * Web NFC (NDEFReader) only reads NDEF-formatted tags.
 * Biometric passports (ISO 14443-4) require BAC/PACE + APDU commands
 * which are only possible in native apps (iOS CoreNFC / Android IsoDep).
 * This helper covers: NDEF e-IDs, NFC stickers, and structured text chips.
 */

/** Check if Web NFC is supported on this device/browser */
export const checkNfcSupport = () => {
  if (typeof window === "undefined" || !("NDEFReader" in window)) {
    return {
      supported: false,
      reason:
        "Web NFC is not supported on this browser or device. Please use an NFC-enabled Android device with Chrome.",
    };
  }
  return { supported: true };
};

// ─── ICAO 9303 MRZ helpers ───────────────────────────────────────────────────

/** Luhn-style MRZ check digit (0–9 A–Z <) */
const CHECK_WEIGHTS = [7, 3, 1];
const mrzCheckDigit = (str) => {
  const val = (c) => {
    if (c === "<") return 0;
    const n = parseInt(c, 10);
    if (!isNaN(n)) return n;
    return c.charCodeAt(0) - 55; // A=10 … Z=35
  };
  return str.split("").reduce((sum, c, i) => sum + val(c) * CHECK_WEIGHTS[i % 3], 0) % 10;
};

/** Format YYMMDD → YYYY-MM-DD (century heuristic: >30 = 19xx, else 20xx) */
const mrzDate = (yymmdd) => {
  if (!yymmdd || yymmdd === "<<<<<<") return "";
  const yy = parseInt(yymmdd.slice(0, 2), 10);
  const century = yy > 30 ? "19" : "20";
  return `${century}${yymmdd.slice(0, 2)}-${yymmdd.slice(2, 4)}-${yymmdd.slice(4, 6)}`;
};

/** Convert "DOE<<JOHN<MIDDLE" → "John Middle Doe" */
const mrzName = (raw) => {
  const [surname, ...given] = raw.split("<<");
  const fmt = (s) =>
    s
      .replace(/</g, " ")
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  return [fmt(given.join(" ")), fmt(surname)].filter(Boolean).join(" ");
};

/**
 * Parse ICAO 9303 MRZ — supports TD1 (3×30), TD2 (2×36), TD3 (2×44)
 * Returns structured identity fields or null if not valid MRZ.
 */
const parseMrz = (lines) => {
  const clean = lines.map((l) => l.trim().toUpperCase());

  // TD3 — Passport (2 lines × 44 chars)
  if (clean.length >= 2 && clean[0].length === 44 && clean[1].length === 44) {
    const [l1, l2] = clean;
    const docNum = l2.slice(0, 9).replace(/</g, "");
    const dob   = mrzDate(l2.slice(13, 19));
    const exp   = mrzDate(l2.slice(21, 27));
    // Validate key check digits
    const valid =
      mrzCheckDigit(l2.slice(0, 9))  === parseInt(l2[9],  10) &&
      mrzCheckDigit(l2.slice(13, 19)) === parseInt(l2[19], 10) &&
      mrzCheckDigit(l2.slice(21, 27)) === parseInt(l2[27], 10);

    return {
      format: "TD3",
      documentType: l1[0],
      country: l1.slice(2, 5).replace(/</g, ""),
      fullName: mrzName(l1.slice(5, 44)),
      documentNumber: docNum,
      nationality: l2.slice(10, 13).replace(/</g, ""),
      dateOfBirth: dob,
      gender: l2[20] === "M" ? "Male" : l2[20] === "F" ? "Female" : "Other",
      expiryDate: exp,
      checkDigitsValid: valid,
      mrzData: { line1: l1, line2: l2 },
    };
  }

  // TD1 — National ID (3 lines × 30 chars)
  if (clean.length >= 3 && clean[0].length === 30) {
    const [l1, l2, l3] = clean;
    const docNum = l1.slice(5, 14).replace(/</g, "");
    const dob   = mrzDate(l2.slice(0, 6));
    const exp   = mrzDate(l2.slice(8, 14));
    return {
      format: "TD1",
      documentType: l1.slice(0, 2).trim(),
      country: l1.slice(2, 5).replace(/</g, ""),
      documentNumber: docNum,
      dateOfBirth: dob,
      gender: l2[7] === "M" ? "Male" : l2[7] === "F" ? "Female" : "Other",
      expiryDate: exp,
      nationality: l2.slice(15, 18).replace(/</g, ""),
      fullName: mrzName(l3),
      mrzData: { line1: l1, line2: l2, line3: l3 },
    };
  }

  return null; // Not a recognised MRZ format
};

/**
 * Parse raw NFC chip text into structured KYC fields.
 * Priority: JSON → MRZ → key-value fallback
 */
export const parseNfcPayload = (rawText) => {
  if (!rawText) return {};

  // 1. JSON payload (some e-IDs encode data as JSON)
  try {
    const parsed = JSON.parse(rawText);
    if (typeof parsed === "object" && parsed !== null) return parsed;
  } catch (_) {}

  const lines = rawText.split(/[\r\n]+/).filter(Boolean);

  // 2. ICAO 9303 MRZ lines (strict length filter)
  const mrzLines = lines.filter((l) => /^[A-Z0-9<]{30}$|^[A-Z0-9<]{36}$|^[A-Z0-9<]{44}$/.test(l.trim().toUpperCase()));
  const mrzResult = parseMrz(mrzLines);
  if (mrzResult) return { rawPayload: rawText, ...mrzResult };

  // 3. Key-value fallback for NDEF text record e-IDs
  const kv = {};
  for (const line of lines) {
    const [key, ...rest] = line.split(":");
    if (!rest.length) continue;
    const k = key.trim().toLowerCase();
    const v = rest.join(":").trim();
    if (/name/.test(k))           kv.fullName       = v;
    if (/doc|passport|id/.test(k)) kv.documentNumber = v;
    if (/dob|birth/.test(k))      kv.dateOfBirth    = v;
    if (/expir/.test(k))          kv.expiryDate     = v;
    if (/country|nation/.test(k)) { kv.country = v; kv.nationality = v; }
    if (/gender|sex/.test(k))     kv.gender         = v;
  }

  return Object.keys(kv).length
    ? { rawPayload: rawText, ...kv }
    : { rawPayload: rawText };
};

/**
 * Read NFC chip data from an ID document using Web NFC NDEFReader
 * @param {Function} onProgress Optional callback for live progress updates (stageText, percent)
 * @returns {Promise<Object>} { success, data, error }
 */
export const readNfcChip = async (onProgress = () => {}) => {
  const support = checkNfcSupport();
  if (!support.supported) {
    return { success: false, data: null, error: support.reason };
  }

  // AbortController — scan band karta hai jab data aa jaye, error ho, ya timeout ho
  const abortController = new AbortController();
  const SCAN_TIMEOUT_MS = 30000; // 30 seconds max wait

  try {
    const NDEFReaderClass = window.NDEFReader;
    const reader = new NDEFReaderClass();
    await reader.scan({ signal: abortController.signal });
    // reader.scan() succeeded — hardware active, chip touch ka wait
    // Do NOT call onProgress here — no real data yet

    return new Promise((resolve) => {
      let resolved = false;

      // Auto-stop agar 30 second mein koi card touch na ho
      const timeoutId = setTimeout(() => {
        if (resolved) return;
        resolved = true;
        abortController.abort();
        resolve({
          success: false,
          data: null,
          error: "Scan timed out. Please hold your document closer to the phone and try again.",
        });
      }, SCAN_TIMEOUT_MS);

      const finish = (result) => {
        if (resolved) return;
        resolved = true;
        clearTimeout(timeoutId);
        // Scan immediately stop karo — card remove hone ke baad bhi scanner band ho jaye
        abortController.abort();
        resolve(result);
      };

      reader.onreading = (event) => {
        // REAL event: chip physically touched phone — only NOW progress updates
        onProgress("Chip detected! Reading data blocks...", 70);

        const decoder = new TextDecoder();
        let extractedText = "";
        const records = [];

        if (event.message && event.message.records) {
          for (const record of event.message.records) {
            let recordData = "";
            if (record.data) {
              recordData = decoder.decode(record.data);
              extractedText += recordData + "\n";
            }
            records.push({
              recordType: record.recordType,
              mediaType: record.mediaType,
              id: record.id,
              data: recordData,
            });
          }
        }

        onProgress("Parsing identity fields & MRZ...", 90);
        const structuredData = parseNfcPayload(extractedText.trim());
        onProgress("Chip verified successfully!", 100);

        finish({
          success: true,
          data: {
            serialNumber: event.serialNumber || `NFC-${Date.now()}`,
            rawRecords: records,
            ...structuredData,
          },
          error: null,
        });
      };

      reader.onreadingerror = () => {
        finish({
          success: false,
          data: null,
          error: "Failed to read NFC tag. Please keep document steady against the device.",
        });
      };
    });
  } catch (error) {
    // AbortError is expected — ignore it silently
    if (error.name === "AbortError") {
      return { success: false, data: null, error: "Scan was cancelled." };
    }
    return {
      success: false,
      data: null,
      error: error.message || "Failed to start NFC scan.",
    };
  }
};
